import React, {useMemo} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useTheme} from '../helpers/ThemeContext';
import {useLastTab} from '../helpers/useLastTab';
import {PREMIUM_TAB_SCREEN_OPTIONS, renderPremiumTabBar} from '../components/premium/PremiumTabBar';
import {useServiceMode} from '../helpers/ServiceModeContext';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';

// Screens
import RepairmanDashboardScreen from './RepairmanDashboardScreen';
import WorkSheetListScreen from './WorkSheetListScreen';
import RepairmanCalendarScreen from './RepairmanCalendarScreen';
import RepairmanMoreScreen from './RepairmanMoreScreen';
import PushNotificationsBootstrap from '../components/PushNotificationsBootstrap';
import {useNavigation} from '@react-navigation/native';

const Tab = createBottomTabNavigator();

export default function RepairmanHomeScreen() {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const navigation = useNavigation();
    const {isFeatureEnabled} = useServiceMode();
    // Team Chat lives under "Egyéb" — surface its unread count on that tab.
    // Messenger hub entry (More tab badge): team chat + live support unread combined.
    const {visible: teamChatVisible, total: teamChatUnread} = useChatHubBadge();
    const dashboardEnabled = isFeatureEnabled('dashboard');
    const bookingsEnabled = isFeatureEnabled('bookings');
    const validTabs = useMemo(() => [
        ...(dashboardEnabled ? ['RepairDashboard'] : []),
        'WorkSheets',
        ...(bookingsEnabled ? ['RepairCalendar'] : []),
        'RepairMore',
    ], [dashboardEnabled, bookingsEnabled]);
    const {ready: lastTabReady, initialRouteName: lastTab, screenListeners: tabListeners} = useLastTab('service', {
        fallback: dashboardEnabled ? 'RepairDashboard' : 'WorkSheets',
        valid: validTabs,
    });


    if (!lastTabReady) {
        return (
            <View style={[styles.center, {backgroundColor: theme.colors.surface}]}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
        );
    }

    return (
        <>
            <PushNotificationsBootstrap navigation={navigation} />
            <Tab.Navigator
                initialRouteName={lastTab || (dashboardEnabled ? 'RepairDashboard' : 'WorkSheets')}
                screenListeners={tabListeners}
                tabBar={renderPremiumTabBar}
                screenOptions={PREMIUM_TAB_SCREEN_OPTIONS}
            >
                {dashboardEnabled && (
                <Tab.Screen
                    name="RepairDashboard"
                    component={RepairmanDashboardScreen}
                    options={{
                        tabBarLabel: t('repair_dashboard') || 'Áttekintés',
                        tabBarIcon: ({ color, size }) => (
                            <Icon name="view-dashboard" color={color} size={size} />
                        ),
                    }}
                />
                )}
                <Tab.Screen
                    name="WorkSheets"
                    component={WorkSheetListScreen}
                    options={{
                        tabBarLabel: t('repair_worksheets') || 'Munkalapok',
                        tabBarIcon: ({ color, size }) => (
                            <Icon name="clipboard-list" color={color} size={size} />
                        ),
                    }}
                />
                {bookingsEnabled && (
                <Tab.Screen
                    name="RepairCalendar"
                    component={RepairmanCalendarScreen}
                    options={{
                        tabBarLabel: t('repair_calendar') || 'Naptár',
                        tabBarIcon: ({ color, size }) => (
                            <Icon name="calendar-clock" color={color} size={size} />
                        ),
                    }}
                />
                )}
                <Tab.Screen
                    name="RepairMore"
                    component={RepairmanMoreScreen}
                    options={{
                        tabBarLabel: t('repair_more') || 'Egyéb',
                        tabBarBadge: teamChatVisible && teamChatUnread > 0 ? (teamChatUnread > 99 ? '99+' : teamChatUnread) : undefined,
                        tabBarBadgeStyle: {backgroundColor: '#F97316', color: '#FFFFFF', fontSize: 10, fontWeight: '800'},
                        tabBarIcon: ({ color, size }) => (
                            <Icon name="dots-horizontal" color={color} size={size} />
                        ),
                    }}
                />
            </Tab.Navigator>
        </>
    );
}

const styles = StyleSheet.create({
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});


